// =============================================================
// WAVE 4 — Tasks register (the strategic global grid).
//
// Distinct from the tactical Work board: this is the COMPLETE register of all
// work orders, paged server-side over GET /tasks (limit/offset), with sortable
// columns, client-side filters over the loaded page, multi-select + a CSV
// export of the selected/filtered rows, and a row → WO detail drawer (the
// Wave-3 WoDetail, opened inline as the board does).
//
// HONEST API NOTES:
//   - Paging is real (limit/offset). `total` is shown only when the server
//     reports it; otherwise we show the loaded range and offer Next while the
//     page is full — we never fabricate a total.
//   - Sort + free-text + status/priority/overdue filters operate over the
//     LOADED page (the documented contract for this wave); if the list endpoint
//     later grows server filters they can be wired without UI change.
//   - CSV export is a client-side Blob download (no backend needed).
// =============================================================

import { useMemo, useState } from "react";
import { relativeOverdue, type StatusKind, type Task, type TaskStatus } from "@perpetuum/core";
import { useCollection, useDevices, useTasksPage } from "../lib/queries.js";
import { useScopedT } from "../lib/i18n-keys.js";
import { Btn, Icon, ICONS, StatusChip } from "../components/primitives.js";
import { EmptyState } from "../components/EmptyState.js";
import { Skeleton } from "../components/Skeleton.js";
import { WoDetail } from "./WoDetail.js";

type SortKey = "wo" | "title" | "device" | "status" | "priority" | "assignee" | "due";
type SortDir = "asc" | "desc";
type StatusFilter = "all" | TaskStatus;
type PrioFilter = "all" | "low" | "normal" | "high" | "critical";

const PAGE_SIZE = 25;

// Status → [chip kind, i18n key]. Labels are resolved through t() at render
// time (canonical `status.*` keys, shared with the work board) so the chips
// localize rather than showing hardcoded English.
const STATUS_CHIP: Record<TaskStatus, [StatusKind, string]> = {
  open: ["info", "status.open"],
  in_progress: ["warning", "status.in_progress"],
  paused: ["paused", "status.paused"],
  completed: ["success", "status.completed"],
  cancelled: ["paused", "status.cancelled"],
  postponed: ["paused", "status.postponed"],
};
const PRIO_CHIP: Record<string, string> = {
  critical: "critical",
  high: "warning",
  normal: "paused",
  low: "paused",
};
const STATUS_RANK: Record<TaskStatus, number> = {
  open: 0,
  in_progress: 1,
  paused: 2,
  postponed: 3,
  completed: 4,
  cancelled: 5,
};
const PRIO_RANK: Record<string, number> = { critical: 0, high: 1, normal: 2, low: 3 };

/** Stable comparison: numbers numerically, otherwise localeCompare. */
function cmp(a: number | string, b: number | string): number {
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a).localeCompare(String(b));
}

/** Is this task overdue at snapshot time? (dueDate in the past, or dueDays > 0). */
function isOverdue(task: Task): boolean {
  if (task.isCompleted || task.status === "completed") return false;
  if (task.dueDate) return Date.parse(task.dueDate) < Date.now();
  return (task.dueDays ?? 0) > 0;
}

/** RFC-4180 cell escaping (quote when the value contains a comma/quote/newline). */
function csvCell(value: string): string {
  return /[",\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

export function TasksRegister() {
  const t = useScopedT();
  const usersQ = useCollection("users");
  const devicesQ = useDevices();

  const [page, setPage] = useState(0);
  const tasksQ = useTasksPage(PAGE_SIZE, page * PAGE_SIZE);

  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [prioFilter, setPrioFilter] = useState<PrioFilter>("all");
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [query, setQuery] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("due");
  const [sortDir, setSortDir] = useState<SortDir>("asc");
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [selected, setSelected] = useState<Task | null>(null);

  const users = usersQ.data ?? [];
  const devices = devicesQ.data ?? [];
  // Resolve a user id → display name. A deleted/unknown id must never surface as
  // a raw UUID in the assignee column — fall back to a localized "(unknown)".
  const userName = (id: string) => {
    const u = users.find((x) => x.id === id);
    return u?.name != null ? String(u.name) : t("common.unknownUser");
  };
  const deviceName = (id: string): string | undefined => devices.find((d) => d.id === id)?.name;
  const assigneeLabel = (task: Task): string => {
    const ids = task.assignedUserIds ?? [];
    return ids.length > 0 ? ids.map(userName).join(", ") : t("register.unassigned");
  };

  const pageRows = tasksQ.data?.rows ?? [];
  const total = tasksQ.data?.total ?? null;

  // Client-side filter over the loaded page (the wave's documented contract).
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return pageRows.filter((task) => {
      if (statusFilter !== "all" && task.status !== statusFilter) return false;
      if (prioFilter !== "all" && (task.priority ?? "normal") !== prioFilter) return false;
      if (overdueOnly && !isOverdue(task)) return false;
      if (q) {
        const hay = `${task.number ?? ""} ${task.title}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [pageRows, statusFilter, prioFilter, overdueOnly, query]);

  const rows = useMemo(() => {
    const keyOf = (task: Task): number | string => {
      switch (sortKey) {
        case "wo":
          return (task.number ?? task.id).toLowerCase();
        case "title":
          return task.title.toLowerCase();
        case "device":
          return (task.deviceId ? deviceName(task.deviceId) ?? task.deviceId : "").toLowerCase();
        case "status":
          return STATUS_RANK[task.status] ?? 99;
        case "priority":
          return PRIO_RANK[task.priority ?? "normal"] ?? 2;
        case "assignee":
          return assigneeLabel(task).toLowerCase();
        case "due":
          return task.dueDate
            ? Date.parse(task.dueDate)
            : task.dueDays != null
              ? Date.now() + task.dueDays * 86_400_000
              : Number.MAX_SAFE_INTEGER;
      }
    };
    const sorted = [...filtered].sort((a, b) => cmp(keyOf(a), keyOf(b)));
    return sortDir === "asc" ? sorted : sorted.reverse();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered, sortKey, sortDir, devices, users]);

  const visibleIds = useMemo(() => new Set(rows.map((r) => r.id)), [rows]);
  const checkedVisible = useMemo(() => [...checked].filter((id) => visibleIds.has(id)), [checked, visibleIds]);
  const allChecked = rows.length > 0 && checkedVisible.length === rows.length;
  const someChecked = checkedVisible.length > 0 && !allChecked;

  const toggleSort = (key: SortKey) => {
    if (key === sortKey) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDir("asc");
    }
  };
  const ariaSort = (key: SortKey): "ascending" | "descending" | "none" =>
    key === sortKey ? (sortDir === "asc" ? "ascending" : "descending") : "none";

  const toggleRow = (id: string) =>
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const toggleAll = () =>
    setChecked((prev) => {
      const next = new Set(prev);
      if (allChecked) rows.forEach((r) => next.delete(r.id));
      else rows.forEach((r) => next.add(r.id));
      return next;
    });

  // CSV over the selected rows (or the full filtered set when nothing checked).
  const onExport = () => {
    const exportRows = checkedVisible.length > 0 ? rows.filter((r) => checked.has(r.id)) : rows;
    const header = [
      t("register.col.wo"),
      t("register.col.title"),
      t("register.col.device"),
      t("register.col.status"),
      t("register.col.priority"),
      t("register.col.assignee"),
      t("register.col.due"),
      t("register.col.overdue"),
    ];
    const lines = [header.join(",")];
    for (const task of exportRows) {
      lines.push(
        [
          task.number ?? task.id,
          task.title,
          task.deviceId ? deviceName(task.deviceId) ?? task.deviceId : "",
          task.status,
          task.priority ?? "",
          assigneeLabel(task),
          task.dueDate ? new Date(task.dueDate).toISOString().slice(0, 10) : "",
          isOverdue(task) ? "yes" : "no",
        ]
          .map((c) => csvCell(String(c)))
          .join(","),
      );
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `tasks-register-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const STATUS_OPTS: [StatusFilter, string][] = [
    ["all", "register.filter.allStatus"],
    ["open", "workboard.filter.open"],
    ["in_progress", "workboard.filter.in_progress"],
    ["paused", "register.status.paused"],
    ["completed", "workboard.filter.completed"],
    ["cancelled", "register.status.cancelled"],
    ["postponed", "register.status.postponed"],
  ];
  const PRIO_OPTS: [PrioFilter, string][] = [
    ["all", "register.filter.allPriority"],
    ["critical", "register.prio.critical"],
    ["high", "register.prio.high"],
    ["normal", "register.prio.normal"],
    ["low", "register.prio.low"],
  ];

  const COLS: { key: SortKey; labelKey: string; width?: number }[] = [
    { key: "wo", labelKey: "register.col.wo", width: 130 },
    { key: "title", labelKey: "register.col.title" },
    { key: "device", labelKey: "register.col.device" },
    { key: "status", labelKey: "register.col.status", width: 130 },
    { key: "priority", labelKey: "register.col.priority", width: 100 },
    { key: "assignee", labelKey: "register.col.assignee" },
    { key: "due", labelKey: "register.col.due", width: 150 },
  ];

  // Range label: 1-based inclusive over the current page's filtered rows.
  const fromIdx = page * PAGE_SIZE + (pageRows.length > 0 ? 1 : 0);
  const toIdx = page * PAGE_SIZE + pageRows.length;
  const pageFull = pageRows.length === PAGE_SIZE;
  const hasNext = total != null ? toIdx < total : pageFull;

  return (
    <div>
      <h2 className="d-h2">{t("register.title")}</h2>
      <p className="d-sub">{t("register.sub")}</p>

      <div className="wb-filters" style={{ flexWrap: "wrap", gap: 10 }}>
        <div className="field-ctrl" style={{ width: 240 }}>
          <Icon d={ICONS.search} size={16} style={{ color: "var(--sys-fg-3)" }} />
          <input
            type="search"
            aria-label={t("register.search")}
            placeholder={t("register.search")}
            data-testid="reg-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <div className="field-ctrl" style={{ width: 170 }}>
          <select
            aria-label={t("register.filter.status")}
            data-testid="reg-status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
            style={selectStyle}
          >
            {STATUS_OPTS.map(([k, labelKey]) => (
              <option key={k} value={k}>
                {t(labelKey)}
              </option>
            ))}
          </select>
        </div>
        <div className="field-ctrl" style={{ width: 150 }}>
          <select
            aria-label={t("register.filter.priority")}
            data-testid="reg-priority"
            value={prioFilter}
            onChange={(e) => setPrioFilter(e.target.value as PrioFilter)}
            style={selectStyle}
          >
            {PRIO_OPTS.map(([k, labelKey]) => (
              <option key={k} value={k}>
                {t(labelKey)}
              </option>
            ))}
          </select>
        </div>
        <label className="toggle-line">
          <button
            className={`toggle ${overdueOnly ? "on" : ""}`}
            role="switch"
            aria-checked={overdueOnly}
            aria-label={t("register.filter.overdue")}
            data-testid="reg-overdue"
            onClick={() => setOverdueOnly((v) => !v)}
          >
            <span className="knob" />
          </button>
          {t("register.filter.overdue")}
        </label>
        <span style={{ flex: 1 }} />
        <Btn variant="secondary" size="sm" data-testid="reg-export" disabled={rows.length === 0} onClick={onExport}>
          <Icon d={ICONS.upload} size={15} />
          {checkedVisible.length > 0
            ? t("register.export.selected", { n: checkedVisible.length })
            : t("register.export.all")}
        </Btn>
      </div>

      <div className="d-card">
        <div className="d-card-body" style={{ padding: 0 }}>
          <table className="d-table" data-testid="reg-table">
            <thead>
              <tr>
                <th style={{ width: 34 }}>
                  <input
                    type="checkbox"
                    aria-label={t("register.selectAll")}
                    data-testid="reg-select-all"
                    checked={allChecked}
                    ref={(el) => {
                      if (el) el.indeterminate = someChecked;
                    }}
                    onChange={toggleAll}
                    disabled={rows.length === 0}
                  />
                </th>
                {COLS.map(({ key, labelKey, width }) => (
                  <th key={key} style={width ? { width } : undefined} aria-sort={ariaSort(key)}>
                    <button
                      type="button"
                      className={`wb-sort ${sortKey === key ? "on" : ""}`}
                      data-testid={`reg-sort-${key}`}
                      onClick={() => toggleSort(key)}
                    >
                      {t(labelKey)}
                      {sortKey === key && (
                        <Icon
                          d={sortDir === "asc" ? ICONS.chevronUp : ICONS.chevronDown}
                          size={13}
                          sw={2.4}
                          style={{ marginLeft: 4 }}
                        />
                      )}
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tasksQ.isLoading && <Skeleton variant="table-row" count={6} cols={8} />}

              {!tasksQ.isLoading &&
                rows.map((task) => {
                  const [chipKind, chipKey] = STATUS_CHIP[task.status] ?? (["paused", null] as const);
                  const chipLabel = chipKey ? t(chipKey) : task.status;
                  const overdue = isOverdue(task);
                  const intervalUnits = task.interval?.calendarDays ?? task.interval?.counterHours ?? 0;
                  const late = task.dueDays && task.dueDays > 0 ? task.dueDays : 0;
                  const ov = intervalUnits > 0 && late > 0 ? relativeOverdue(late, intervalUnits) : null;
                  const isChecked = checked.has(task.id);
                  return (
                    <tr
                      key={task.id}
                      className={`rowhover ${isChecked ? "checked" : ""}`}
                      data-testid={`reg-row-${task.id}`}
                    >
                      <td>
                        <input
                          type="checkbox"
                          aria-label={t("register.selectRow", { wo: task.number ?? task.title })}
                          data-testid={`reg-check-${task.id}`}
                          checked={isChecked}
                          onChange={() => toggleRow(task.id)}
                        />
                      </td>
                      <td className="mono">
                        <button
                          type="button"
                          className="reg-wo-link"
                          data-testid={`reg-open-${task.id}`}
                          onClick={() => setSelected(task)}
                          style={linkBtnStyle}
                        >
                          {task.number ?? task.id.slice(0, 8)}
                        </button>
                      </td>
                      <td style={{ fontWeight: 600 }}>{task.title}</td>
                      <td>{task.deviceId ? deviceName(task.deviceId) ?? task.deviceId : "—"}</td>
                      <td>
                        <StatusChip kind={chipKind}>{chipLabel}</StatusChip>
                      </td>
                      <td>
                        {task.priority ? (
                          <span className={`prio-chip ${PRIO_CHIP[task.priority]}`}>{task.priority}</span>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td>{assigneeLabel(task)}</td>
                      <td>
                        <span style={{ display: "inline-flex", gap: 8, alignItems: "center" }}>
                          {task.dueDate ? new Date(task.dueDate).toLocaleDateString() : "—"}
                          {overdue && (
                            <span
                              className={`prio-chip ${ov && ov.band === "alarm" ? "critical" : "warning"}`}
                              data-testid={`reg-overdue-${task.id}`}
                              title={t("register.overdueTitle")}
                            >
                              {late > 0 ? t("register.overdueDays", { n: late }) : t("register.overdueFlag")}
                            </span>
                          )}
                        </span>
                      </td>
                    </tr>
                  );
                })}

              {!tasksQ.isLoading && rows.length === 0 && (
                <tr>
                  <td colSpan={8} style={{ padding: 0 }}>
                    <EmptyState icon={ICONS.list} title={t("register.empty")} hint={t("register.emptyHint")} compact />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="reg-pager" style={pagerStyle} data-testid="reg-pager">
        <span className="reg-range" data-testid="reg-range" style={{ color: "var(--sys-fg-3)", fontSize: 13 }}>
          {pageRows.length === 0
            ? t("register.range.none")
            : total != null
              ? t("register.range.of", { from: fromIdx, to: toIdx, total })
              : t("register.range.window", { from: fromIdx, to: toIdx })}
        </span>
        <span style={{ flex: 1 }} />
        <Btn variant="ghost" size="sm" data-testid="reg-prev" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>
          <Icon d={ICONS.collapse} size={14} />
          {t("register.prev")}
        </Btn>
        <Btn variant="ghost" size="sm" data-testid="reg-next" disabled={!hasNext} onClick={() => setPage((p) => p + 1)}>
          {t("register.next")}
          <Icon d={ICONS.expand} size={14} />
        </Btn>
      </div>

      {selected && (
        <WoDetail task={selected} onClose={() => setSelected(null)} userName={userName} deviceName={deviceName} />
      )}
    </div>
  );
}

const selectStyle = {
  border: 0,
  background: "none",
  outline: "none",
  font: "inherit",
  color: "inherit",
  width: "100%",
} as const;

const linkBtnStyle = {
  border: 0,
  background: "none",
  font: "inherit",
  color: "var(--brand-primary)",
  fontWeight: 700,
  cursor: "pointer",
  padding: 0,
} as const;

const pagerStyle = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  marginTop: 14,
} as const;
